import { getHttpClient } from '@vnetwork/margarita-core/http';
import {
  COMPUTE_WORKSPACE_KEY,
  defineWorkspaceService,
  fetchSharedWorkspaces,
  iamKeys,
  normalizeId,
  readComputeWorkspace,
  type OrgWorkspace,
  type SharedWorkspace,
  type WorkspaceClient,
} from '@vnetwork/margarita-shell/iam';
import { computePaths } from './paths';

/** The service type the registry and `resolveOrganizationWorkspace` know Compute by. */
export const COMPUTE_WORKSPACE_TYPE = 'compute';

interface HomeWorkspace {
  name?: string;
  organization_id: string;
  metadata_id?: string;
}

/** The shared workspaces of other organizations, as the registry lists them: `ref` is the metadata id the console URL carries. */
export function sharedWorkspaceEntries(list: readonly (Pick<SharedWorkspace, 'organization_id' | 'metadata_id'> & Partial<SharedWorkspace>)[]): OrgWorkspace[] {
  return list.map((w) => {
    const projectIds = w.project_ids ?? [];
    const resourceIds = w.machine_ids ?? [];
    return {
      serviceType: COMPUTE_WORKSPACE_TYPE,
      organizationId: normalizeId(w.organization_id),
      organizationName: w.name ?? '',
      home: false,
      ref: normalizeId(w.metadata_id),
      // Compute answers an organization-level role as a workspace with neither projects nor machines.
      scope: projectIds.length > 0 ? 'projects' : resourceIds.length > 0 ? 'resources' : 'org',
      projectIds,
      resourceIds,
    };
  });
}

function homeEntries(raw: HomeWorkspace | null): OrgWorkspace[] {
  if (!raw) return [];
  return [
    {
      serviceType: COMPUTE_WORKSPACE_TYPE,
      organizationId: normalizeId(raw.organization_id),
      organizationName: raw.name ?? '',
      home: true,
      ref: normalizeId(raw.metadata_id),
      scope: 'org',
      projectIds: [],
      resourceIds: [],
    },
  ];
}

export function createComputeWorkspaceService(client: WorkspaceClient) {
  return defineWorkspaceService<HomeWorkspace | null, SharedWorkspace[]>({
    type: COMPUTE_WORKSPACE_TYPE,
    home: {
      queryKey: COMPUTE_WORKSPACE_KEY,
      staleTime: 60_000,
      fetch: async () => readComputeWorkspace<HomeWorkspace>((await client.get('/workspace')).data),
    },
    // The key and the answer of `useSharedWorkspaces`: the registry and that hook share one fetch.
    shared: {
      queryKey: iamKeys.mySharedWorkspaces,
      staleTime: 5 * 60_000,
      fetch: () => fetchSharedWorkspaces(client),
    },
    homeEntries,
    sharedEntries: sharedWorkspaceEntries,
    // An entry without a ref cannot be opened: the landing, never the person's own workspace.
    href: (ws, page) => (ws.ref ? `${computePaths.service(ws.ref)}/${page}` : computePaths.base),
  });
}

// The client is looked up per call, not at import: this file is in the entry chunk (module.ts) and the registry is filled at boot.
export const workspaceService = createComputeWorkspaceService({ get: (url) => getHttpClient('computeApi').get(url) });
